/* src/modules/hr/CareerProgressionReport.js */
import React, { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { SearchIcon, DownloadIcon } from '../../components/icons/Icons';

export default function CareerProgressionReport({ appId }) {
    const [reportData, setReportData] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // --- UI CONTROLS STATE ---
    const [employeeStatusFilter, setEmployeeStatusFilter] = useState('Approved'); // 'Approved' = Current, 'Deactivated' = Past
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // --- FETCH DATA (Fetch ALL users so columns remain stable) ---
    useEffect(() => {
        setIsLoading(true);
        const usersCollectionRef = collection(db, `/artifacts/${appId}/users`);
        
        const unsub = onSnapshot(usersCollectionRef, (snapshot) => {
            let allYears = new Set();
            let masterData = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const journey = data.journey || [];
                
                // Filter ONLY for Promotions and Compensation Changes
                const relevantEvents = journey.filter(e => 
                    ['Promotion', 'Compensation Change', 'Increment', 'Bonus'].includes(e.type)
                );

                let userYears = {};
                
                relevantEvents.forEach(event => {
                    if (!event.date) return;
                    
                    const dateObj = new Date(event.date);
                    if (isNaN(dateObj.getTime())) return;

                    const year = dateObj.getFullYear();
                    allYears.add(year);

                    if (!userYears[year]) userYears[year] = [];
                    
                    userYears[year].push({
                        month: dateObj.toLocaleString('default', { month: 'short' }),
                        dateObj: dateObj, 
                        type: event.type,
                        title: event.title,
                        newDesignation: event.newDesignation
                    });
                });

                // Sort events within each year by month (descending)
                Object.keys(userYears).forEach(year => {
                    userYears[year].sort((a, b) => b.dateObj - a.dateObj);
                });

                // ALWAYS push the user to the master list
                masterData.push({
                    id: doc.id,
                    name: data.name || 'Unnamed Employee',
                    department: data.department || 'N/A',
                    designation: data.designation || 'N/A',
                    status: data.status || 'Pending',
                    eventsByYear: userYears
                });
            });

            if (allYears.size === 0) {
                allYears.add(new Date().getFullYear());
            }

            setAvailableYears(Array.from(allYears).sort((a, b) => b - a));
            setReportData(masterData.sort((a, b) => a.name.localeCompare(b.name)));
            setIsLoading(false);
        });

        return () => unsub();
    }, [appId]);

    // --- SEARCH AND STATUS FILTER LOGIC ---
    const filteredData = useMemo(() => {
        // 1. Filter by Employee Status
        let filtered = reportData.filter(user => user.status === employeeStatusFilter);

        // 2. Filter by Search Query
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            filtered = filtered.filter(user => 
                user.name?.toLowerCase().includes(lowerQuery) ||
                user.department?.toLowerCase().includes(lowerQuery) ||
                user.designation?.toLowerCase().includes(lowerQuery)
            );
        }
        
        return filtered;
    }, [reportData, employeeStatusFilter, searchQuery]);

    // --- PAGINATION LOGIC ---
    const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
    
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, itemsPerPage, employeeStatusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(start, start + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);


    // --- CSV EXPORT LOGIC ---
    const exportToCSV = () => {
        if (filteredData.length === 0) return alert("No data available to export.");

        // 1. Setup CSV Headers
        const headers = ['Employee Name', 'Department', 'Designation', 'Status', ...availableYears];
        const csvRows = [headers.join(',')];

        // 2. Map data to rows
        filteredData.forEach(user => {
            const row = [
                `"${user.name}"`,
                `"${user.department}"`,
                `"${user.designation}"`,
                `"${user.status}"`
            ];

            // 3. Map dynamic year columns
            availableYears.forEach(year => {
                if (user.eventsByYear[year] && user.eventsByYear[year].length > 0) {
                    // Combine multiple events in a single year into one string separated by ' | '
                    const eventsStr = user.eventsByYear[year].map(e => {
                        const detail = e.type === 'Promotion' && e.newDesignation ? `Role: ${e.newDesignation}` : e.title;
                        return `${e.month} - ${e.type}: ${detail}`;
                    }).join(' | ');
                    row.push(`"${eventsStr}"`);
                } else {
                    row.push('""'); // Empty cell if no events that year
                }
            });

            csvRows.push(row.join(','));
        });

        // 4. Trigger file download
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `Career_Progression_Report_${new Date().toLocaleDateString().replace(/\//g, '-')}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // --- RENDER HELPERS ---
    const EventPill = ({ event }) => {
        const isPromo = event.type === 'Promotion';
        return (
            <div className={`mb-2 p-2 rounded border text-xs leading-snug ${isPromo ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'}`}>
                <div className="flex justify-between items-start mb-1">
                    <span className={`font-bold uppercase tracking-wider text-[10px] ${isPromo ? 'text-purple-700' : 'text-orange-700'}`}>
                        {event.month} - {isPromo ? 'Promotion' : 'Comp Change'}
                    </span>
                </div>
                <div className="font-medium text-gray-800">
                    {isPromo && event.newDesignation ? `Role: ${event.newDesignation}` : event.title}
                </div>
            </div>
        );
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-[#213368]">Career Progression & Compensation Report</h2>
                <p className="text-sm text-gray-500 mt-1">A consolidated view of all promotions and compensation changes logged in the employee journey timeline.</p>
            </div>

            {/* --- CONTROL BAR --- */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
                {/* Search */}
                <div className="relative w-full md:w-1/3">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search name, dept, or role..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20BEC6] outline-none"
                    />
                </div>

                {/* Filters & Export */}
                <div className="flex w-full md:w-auto items-center gap-4 justify-end">
                    <select
                        value={employeeStatusFilter}
                        onChange={(e) => setEmployeeStatusFilter(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg text-sm bg-white font-medium text-gray-700 focus:ring-2 focus:ring-[#20BEC6] outline-none"
                    >
                        <option value="Approved">Current Employees</option>
                        <option value="Deactivated">Past Employees</option>
                    </select>

                    <button 
                        onClick={exportToCSV}
                        className="flex items-center px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg hover:bg-green-700 transition shadow-sm whitespace-nowrap"
                    >
                        <DownloadIcon className="w-4 h-4 mr-2" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* --- TABLE CONTENT --- */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="overflow-x-auto w-full">
                    <table className="min-w-max w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="sticky left-0 bg-gray-50 z-20 px-6 py-3 text-left text-xs font-bold text-[#213368] uppercase tracking-wider min-w-[250px] max-w-[250px] shadow-[1px_0_0_0_#e5e7eb]">
                                    Employee
                                </th>
                                {availableYears.map(year => (
                                    <th key={year} className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider min-w-[250px] w-[250px]">
                                        {year}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedData.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="sticky left-0 bg-white z-10 px-6 py-4 shadow-[1px_0_0_0_#e5e7eb] align-top min-w-[250px] max-w-[250px] whitespace-normal">
                                        <div className="font-bold text-[#213368]">{user.name}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">{user.designation}</div>
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{user.department}</div>
                                    </td>

                                    {availableYears.map(year => (
                                        <td key={year} className="px-6 py-4 align-top border-l border-gray-100 min-w-[250px] w-[250px] whitespace-normal">
                                            {user.eventsByYear[year] && user.eventsByYear[year].length > 0 ? (
                                                <div className="flex flex-col">
                                                    {user.eventsByYear[year].map((event, i) => (
                                                        <EventPill key={i} event={event} />
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-300 text-sm italic">-</span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                            {paginatedData.length === 0 && (
                                <tr>
                                    <td colSpan={availableYears.length + 1} className="px-6 py-8 text-center text-gray-500 italic">
                                        No employees found matching your search and filter criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* --- PAGINATION FOOTER --- */}
                <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center text-sm text-gray-500">
                        <span className="mr-2">Rows per page:</span>
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="border border-gray-300 rounded p-1 bg-white outline-none focus:ring-1 focus:ring-[#20BEC6]"
                        >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>

                    <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">
                            Showing {filteredData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length}
                        </span>
                        <div className="flex space-x-1">
                            <button 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border border-gray-300 rounded text-sm font-medium bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}